import {getTestProps} from '@benchling/js-test-utils';
import {
  createStyles,
  TextInput as MantineTextInput,
  TextInputProps as MantineTextInputProps,
} from '@mantine/core';
import React, {RefObject, useRef} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import useOnMount from 'ui-library/hooks/useOnMount';
import {omit} from 'util/omitKey';
import {InputChangeEvent, InputFocusEvent} from 'util/Types';

import {Size} from './InputConstants';

const useStyles = createStyles((theme, {size}: {size?: Size}) => ({
  disabledInput: {
    backgroundColor: theme.colors.gray[1],
    borderColor: theme.colors.gray[1],
    // Set on color and placeholder color so the text is the right color for placeholders and values
    color: theme.colors.gray[6],
    cursor: 'not-allowed',
    '::placeholder': {
      color: theme.colors.gray[6],
    },
  },
  hasButtonInput: {
    borderRadius: `${theme.radius.sm} 0 0 ${theme.radius.sm}`,
  },
  input: {
    fontSize: size === Size.SMALL ? theme.fontSizes.md : theme.fontSizes.lg,
    height: size === Size.SMALL ? '28px' : '36px',
    borderRadius: theme.radius.sm,
  },
  error: {
    display: 'none',
  },
  readOnlyInput: {
    // This overrides the default CSS styling for readonly tags, which is to add a gray background
    backgroundColor: theme.white,
  },
}));

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * A class name to apply to the input element. Note that this is not the root of the `TextInput`; in order
   * to style the root, use `rootClassName`instead.
   */
  className?: string;
  /**
   * A class name to apply to the root element. Note that this is not the `input` element itself; in order to
   * style that, use `className` instead.
   */
  rootClassName?: string;
  /**
   * Whether the input has a button on the right, as with a DateTimePicker. This removes the border radius on
   * the right-hand side of the input so it can abut the button directly.
   * @default false
   */
  hasButton?: boolean;
  /**
   * Whether the input has an error. This causes the input to display with a red outline.
   * @default false
   */
  hasError?: boolean;
  /**
   * One of {@link Size.SMALL} or {@link Size.MEDIUM}. If passed, renders the input at a specific font size
   * and component height (13/28 for small, 14/36 for medium.)
   * @default {@link Size.SMALL}
   */
  size?: Size;
  autoFocus?: boolean;
  /**
   * If true, all the text in the input will be selected when the component mounts.
   * @default false
   */
  initialSelect?: boolean;
  onChange?: (e: InputChangeEvent) => void;
  onBlur?: (e: InputFocusEvent) => void;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  dataTestAttrs?: DataTestAttrs;
  inputRef?: RefObject<HTMLInputElement>;
  wrapperProps?: MantineTextInputProps['wrapperProps'];
}

/**
 * A bare HTML `input` with some rudimentary styling
 * @deprecated - Use Mantine's TextInput instead
 */
export default function TextInput({
  autoFocus = false,
  hasButton = false,
  hasError = false,
  initialSelect = false,
  size = Size.SMALL,
  ...otherProps
}: TextInputProps): JSX.Element {
  // You can't conditionally use hooks, so we always do a useRef and just don't use it if we're passed a
  // ref in props.
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = otherProps.inputRef ?? internalRef;

  useOnMount(() => {
    if (initialSelect) {
      ref.current!.select();
    }
  });

  const {classes, cx} = useStyles({size});

  return (
    <MantineTextInput
      classNames={{
        input: cx(
          classes.input,
          otherProps.className,
          'fieldInput-input',
          otherProps.readOnly && classes.readOnlyInput,
          otherProps.disabled && classes.disabledInput,
          hasButton && classes.hasButtonInput,
          hasError && 'fieldInput-input--error'
        ),
        error: classes.error,
        ...(otherProps.rootClassName != null ? {root: otherProps.rootClassName} : {}),
      }}
      autoFocus={autoFocus}
      // Make the input invalid so we can test its invalid state, but don't actually display an error message
      // (and, above, don't even show the error div)
      error={hasError ? ' ' : undefined}
      ref={ref}
      size={size === Size.SMALL ? 'sm' : 'md'}
      {...omit(otherProps, 'className', 'dataTestAttrs', 'inputRef', 'rootClassName')}
      {...getTestProps(otherProps.dataTestAttrs)}
    />
  );
}
